import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Mail } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import Logo from "@/components/layout/logo"
import { usePlatformParams } from "@/hooks/use-platform-params"
import { ROUTES } from "@/config/routes"
import { resetPwSendEmail } from "@/api/auth"
import type { ResetPasswordSendEmailCredentials, ResetPasswordSendEmailResponse } from "@/types/auth"
import type { ApiResponse } from "@/types/api"
import { toast } from 'sonner'

export function PasswordResetRequestPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { platformParams, createUrlWithParams } = usePlatformParams();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('이메일 주소를 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const credentials: ResetPasswordSendEmailCredentials = {
        email
      };

      const platform = platformParams.platform || 'cafe24';
      const response: ApiResponse<ResetPasswordSendEmailResponse> = await resetPwSendEmail(credentials, platform);
      console.log('비밀번호 재설정 이메일 응답:', response);

      // API Response가 성공적이면 code가 200이거나 2xx 범위
      if (response.code && response.code >= 200 && response.code < 300) {
        setIsSuccess(true);
        toast.success('비밀번호 재설정 링크를 이메일로 전송했습니다.');
        
        // 성공 메시지 표시 후 3초 뒤 로그인 페이지로 이동
        setTimeout(() => {
          navigate(createUrlWithParams(ROUTES.SIGNIN));
        }, 3000);
      } else if (response.code === 2014) {
        toast.error(
          <div>
            입력한 이메일 주소로 가입된 정보가 없습니다.<br />
            이메일 주소를 확인해주세요.
          </div>
        );
      } else {
        toast.error(response.message || '이메일 전송에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 재설정 이메일 전송 오류:', error);
      toast.error('이메일 전송 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl">
            {isSuccess ? '이메일을 전송했습니다!' : '비밀번호를 잊으셨나요?'}
          </CardTitle>
          <CardDescription>
            {isSuccess ? (
              <>
                입력하신 이메일로 비밀번호 재설정 링크를 전송했습니다. <br />
                이메일을 확인해주세요. 잠시 후 로그인 페이지로 이동합니다.
              </>
            ) : (
              <>
                가입된 이메일로 비밀번호 재설정 링크를 보내드려요. <br />
                비밀번호 재설정은 10분간 유효합니다.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">이메일 주소</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="이메일 주소를 입력하세요"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "전송 중..." : "이메일 전송하기"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm text-muted-foreground">
                {email}으로 이메일이 전송되었습니다.
              </p>
            </div>
          )}
          
          {!isSuccess && (
            <div className="mt-6 text-center text-sm text-muted-foreground">
              서비스 계정이 있으신가요?{" "}
              <Link 
                className="text-primary hover:text-primary/80 underline"
                to={createUrlWithParams(ROUTES.SIGNIN)}
              >
                로그인하기
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}